import { uniq } from 'lodash';

import { LOCAL_STORAGE_KEYS } from './constants';
import { CourseName } from '../components/admin/GeneralReport/TableSummaryReport/TableSummaryReport.types';

/**
 *  Get course ids for courses associated with the teams passed in
 * @param teams - list of teams
 * @param teamIds - list of selected team ids
 * @returns list of course ids
 */
export const getCourseIdsForSelectedTeams = (
  teams: Optional<ITeam[]>,
  teamIds: string[],
): string[] => {
  if (!teams) {
    return [];
  }

  const courseIds: string[] = [];
  const activeTeams = teams.filter((team) => teamIds.includes(team._id));

  activeTeams.forEach((team: ITeam) => {
    if (!team?.courses) {
      return;
    }

    courseIds.push(...(team.courses as string[]));
  });

  return uniq(courseIds);
};

/**
 * Get course ids for CourseName objects passed in
 * @param courseNames - list of CourseName objects
 * @returns list of course ids
 */
export const getCourseIdsFromCourseNames = (courseNames: Optional<CourseName[]>): string[] => {
  return courseNames?.map((course) => course._id) || [];
};

/**
 * Toggle selection of an item in a list
 * @param items - list of items
 * @param item - item to toggle
 * @returns updated list of items
 * @example
 * const items = ['a', 'b', 'c'];
 * const item = 'b';
 * const updatedItems = toggleSelection(items, item);
 * console.log(updatedItems); // ['a', 'c']
 * @example
 * const items = ['a', 'c'];
 * const item = 'b';
 * const updatedItems = toggleSelection(items, item);
 * console.log(updatedItems); // ['a', 'c', 'b']
 */
export const toggleSelection = (items: string[], item: string) => {
  return items.includes(item)
    ? items.filter((id) => id !== item) // Remove item from items
    : [...items, item]; // Add item to items
};

/**
 * Custom sort function for sorting strings case-insensitively
 * @param rowA - row A
 * @param rowB - row B
 * @param columnId - column id
 * @returns -1 if rowA should come before rowB, 1 if rowA should come after rowB, 0 if they are equal
 */
export const customStringSortCaseInsensitive = (rowA: any, rowB: any, columnId: string) => {
  const valueA = rowA.values[columnId].toLowerCase();
  const valueB = rowB.values[columnId].toLowerCase();

  if (valueA < valueB) return -1;
  if (valueA > valueB) return 1;

  return 0;
};

/**
 * Determine if the search bar should be shown based on the tree data. The search bar should be shown if there is more than one team or if there is one team with children.
 * @param treeData
 * @returns true if the search bar should be shown, false otherwise
 */
export const showSearchBar = (treeData: ITeam[]) => {
  return treeData?.length > 1 || (treeData?.length === 1 && treeData[0].children?.length);
};

// Function to get activeTeams from user object or local storage
/**
 * Get active teams from user object or local storage
 * @param user - The authenticated user
 * @param isTeamManager - Whether the user is a team manager
 * @returns list of active teams
 */
export const getActiveTeams = (user?: IUser, isTeamManager?: boolean) => {
  return isTeamManager
    ? user?.reportTeams
    : JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.activeTeams) || '[]');
};

/**
 * Get active deselected courses from user object or local storage
 * @param user - The authenticated user
 * @param isTeamManager - Whether the user is a team manager
 * @returns list of active deselected courses
 */
export const getActiveDeselectedCourses = (user?: IUser, isTeamManager?: boolean) => {
  return isTeamManager
    ? user?.reportDeselectedCourses
    : JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.activeDeselectedCourses) || '[]');
};
